import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import React from "react";
import { useState, useEffect } from "react";

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false,
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
}

function AdditionalSections() {
  const isMobile = useIsMobile(); // Returns true if screen width < 768

  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const textAlignClass = isArabic
    ? "text-center md:text-right"
    : "text-center md:text-left";

  const options = { triggerOnce: true, threshold: 0.1 };

  const [ref1, inView1] = useInView(options);
  const [ref2, inView2] = useInView(options);
  const [ref3] = useInView(options);

  const slideFromLeft = (inView) => ({
    opacity: inView ? 1 : 0,
    transform: inView ? "translateX(-10%) " : "translateX(-50%) ",
    transition: "opacity 0.8s ease-out, transform 1.5s ease-out",
  });

  const slideFromRight = (inView) => ({
    opacity: inView ? 1 : 0,
    transform: inView ? "translateX(10%)" : "translateX(50%)",
    transition: "opacity 0.8s ease-out, transform 1.5s ease-out",
  });
  const slideFromLeftMob = (inView) => ({
    opacity: inView ? 1 : 0,
    transform: inView ? "translateX(0%) " : "translateX(-50%) ",
    transition: "opacity 0.8s ease-out, transform 1.5s ease-out",
  });

  const slideFromRightMob = (inView) => ({
    opacity: inView ? 1 : 0,
    transform: inView ? "translateX(0%)" : "translateX(50%)",
    transition: "opacity 0.8s ease-out, transform 1.5s ease-out",
  });

  return (
    <div className="canvas-background md:pt-20 pt-0 lg:pb-20 pb-[3rem] bg-white">
      {/* Section 1 - Slide from LEFT */}
      <div
        ref={ref1}
        style={isMobile ? slideFromLeftMob(inView1) : slideFromLeft(inView1)}
        className="md:pb-[0rem] pb-[2rem] md:pt-[0rem] pt-[2rem] w-full max-w-[90rem] h-auto md:h-[30rem] canvas-background bg-[#e0bd4b] container grid grid-cols-1 md:grid-cols-2 gap-8 items-center px-6 md:pr-0 md:pl-[7rem] mx-auto"
      >
        <div className={`order-2 md:order-1 ${textAlignClass} md:pr-[9rem]`}>
          <h2 className="md:text-2xl md:text-xl md:font-semibold text-[1.2rem]  text-[#A85531] mb-4 font-raleway">
            {t("wheatBagPage.additionalSections.section1.title")}
          </h2>
          <p className="text-[#70524D] leading-relaxed font-raleway text-[1rem] md:text-[1.2rem]">
            {t("wheatBagPage.additionalSections.section1.description")}
          </p>
        </div>
        <div className="order-1 md:order-2 overflow-hidden rounded-md h-full">
          <img
            src="/images/wheatBagLavender.webp"
            alt={t("wheatBagPage.additionalSections.section1.imageAlt")}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Section 2 - Slide from RIGHT */}
      <div
        ref={ref2}
        style={isMobile ? slideFromRightMob(inView2) : slideFromRight(inView2)}
        className="md:pb-[0rem] pb-[2rem] md:pt-[0rem] pt-[2rem] w-full max-w-[90rem] h-auto md:h-[30rem] canvas-background bg-[#7d99a4] container grid grid-cols-1 md:grid-cols-2 gap-8 items-center mt-0 md:mt-16 px-6 md:pl-0 md:pr-[7rem] mx-auto"
      >
        <div className="order-1 overflow-hidden h-[101.28%] rounded-md">
          <img
            src="/images/sewingMachineWheatBags.webp"
            alt={t("wheatBagPage.additionalSections.section2.imageAlt")}
            className="w-full h-full object-cover"
          />
        </div>
        <div className={`order-2 ${textAlignClass} md:pl-[9rem]`}>
          <h2 className="md:text-2xl md:text-xl md:font-semibold text-[1.2rem] text-[#A85531] mb-4 font-raleway">
            {t("wheatBagPage.additionalSections.section2.title")}
          </h2>
          <p className="text-[#70524D] leading-relaxed font-raleway text-[1rem] md:text-[1.2rem]">
            {t("wheatBagPage.additionalSections.section2.description")}
          </p>
        </div>
      </div>

      {/* Section 3 - Slide from LEFT */}
      <div
        ref={ref3}
        style={isMobile ? slideFromLeftMob(inView1) : slideFromLeft(inView1)}
        className="md:pb-[0rem] pb-[2rem] md:pt-[0rem] pt-[2rem] w-full max-w-[90rem] h-auto md:h-[30rem] grid grid-cols-1 md:grid-cols-2 gap-8 items-center mt-0 md:mt-16 px-6 md:pr-0 md:pl-[7rem] canvas-background bg-[#e0bd4b] mx-auto"
      >
        <div className={`order-2 md:order-1 ${textAlignClass}  md:pr-[9rem]`}>
          <h2 className="md:text-2xl md:text-xl md:font-semibold text-[1.2rem] text-[#b15d33] mb-4 font-raleway">
            {t("wheatBagPage.additionalSections.section3.title")}
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6 font-raleway text-[1rem] md:text-[1.2rem]">
            {t("wheatBagPage.additionalSections.section3.description")}
          </p>
          <a
            href="/pdfs/instructions.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#b15d33] text-white font-light py-2 px-4 rounded-[5px] shadow-lg hover:bg-[#974504] transition-colors font-raleway"
          >
            {t("wheatBagPage.additionalSections.section3.buttonText")}
          </a>
        </div>
        <div className="order-1 md:order-2 h-full overflow-hidden rounded-md">
          <img
            src="/images/womanWithWheatBag.webp"
            alt={t("wheatBagPage.additionalSections.section3.imageAlt")}
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  );
}

export default AdditionalSections;
